import type { Role } from '@odonix/contracts';

/**
 * Жизненный цикл наряда в зуботехническую лабораторию.
 *
 * Наряд — единственная сущность с двумя равноправными арендаторами: клиника
 * ставит задачу, лаборатория её выполняет, и обе стороны меняют один и тот же
 * объект в течение его жизни. Отсюда — таблица переходов несёт не только «из
 * какого состояния в какое», но и «чья это роль»: без второго измерения любой
 * участник наряда мог бы принять его от лаборатории или закрыть от имени
 * клиники, просто зная, что переход в принципе существует.
 *
 * Правила лежат в ядре, потому что исполняются дважды: на телефоне техника без
 * связи, когда интерфейс решает, показывать ли кнопку «Готово», и на сервере,
 * когда та же операция приходит из очереди через сутки — в наряд, который за
 * это время уже перевели дальше с другого устройства.
 *
 * Модуль: docs/03-modules/06-lab-orders.md
 */

export const LAB_ORDER_STATES = [
  'created',
  'accepted_by_lab',
  'in_progress',
  'try_in_required',
  'remake',
  'ready',
  'received_by_clinic',
  'installed',
  'closed',
  'cancelled',
] as const;
export type LabOrderState = (typeof LAB_ORDER_STATES)[number];

/** Кто виновен в переделке — определяет, чья это ошибка, а не кто платит: расчёт ручной, фаза 2.4. */
export const FAULT_PARTIES = ['clinic', 'lab', 'agreed_change'] as const;
export type FaultParty = (typeof FAULT_PARTIES)[number];

export const RESTORATION_TYPES = ['crown', 'bridge', 'inlay', 'denture', 'braces'] as const;
export type RestorationType = (typeof RESTORATION_TYPES)[number];

export const SHADE_SYSTEMS = ['vita_classical', 'vita_3d_master'] as const;
export type ShadeSystem = (typeof SHADE_SYSTEMS)[number];

export const UNIT_ROLES = ['abutment', 'pontic', 'crown', 'implant_crown'] as const;
export type UnitRole = (typeof UNIT_ROLES)[number];

export interface LabOrderTransition {
  from: LabOrderState;
  to: LabOrderState;
  /** Кто может выполнить именно этот переход — не любой, у кого есть право на наряды вообще. */
  roles: readonly Role[];
}

const CLINIC_ROLES: readonly Role[] = ['clinic_owner', 'clinic_admin'];
/**
 * Клинические шаги у кресла: примерка и установка. Врач — потому что это его
 * работа; владелец — потому что владельцу принадлежат все действия его клиники
 * (см. комментарий к `clinic_owner` в матрице прав): в клинике на одно кресло
 * он сам и врач. Старшего администратора здесь нет — установка конструкции не
 * административное решение.
 */
const CHAIRSIDE_ROLES: readonly Role[] = ['doctor', 'clinic_owner'];
const LAB_MANAGEMENT_ROLES: readonly Role[] = ['lab_owner', 'lab_manager'];
const LAB_WORK_ROLES: readonly Role[] = ['lab_owner', 'lab_manager', 'technician'];
const ANY_PARTICIPANT_ROLES: readonly Role[] = [
  'clinic_owner',
  'clinic_admin',
  'doctor',
  'lab_owner',
  'lab_manager',
  'technician',
];

export const LAB_ORDER_TRANSITIONS: readonly LabOrderTransition[] = [
  // Принятие: лаборатория подтверждает срок и берёт наряд в работу. Врач или
  // регистратура клиники этот переход не выполняют — с этого момента отвечает
  // лаборатория, и решение о сроке принимает она.
  { from: 'created', to: 'accepted_by_lab', roles: LAB_MANAGEMENT_ROLES },
  { from: 'accepted_by_lab', to: 'in_progress', roles: LAB_WORK_ROLES },
  { from: 'in_progress', to: 'try_in_required', roles: LAB_WORK_ROLES },
  // Примерка прошла без переделки — просто доработка. Решает тот, кто видел
  // результат у кресла.
  { from: 'try_in_required', to: 'in_progress', roles: CHAIRSIDE_ROLES },
  { from: 'in_progress', to: 'ready', roles: LAB_WORK_ROLES },
  { from: 'try_in_required', to: 'ready', roles: LAB_WORK_ROLES },
  // Получение и установка — действие клиники и врача, а не лаборатории: с
  // этого момента ответственность за конструкцию у клиники. Не регистратура:
  // у неё нет `lab_order:status:change` — прием посылки от курьера меняет
  // ответственность за конструкцию, а такое решение матрица прав отдаёт
  // owner/admin, а не стойке.
  { from: 'ready', to: 'received_by_clinic', roles: CLINIC_ROLES },
  { from: 'received_by_clinic', to: 'installed', roles: CHAIRSIDE_ROLES },
  { from: 'installed', to: 'closed', roles: CLINIC_ROLES },

  /**
   * Переделка: документ говорит «любой → remake», но не буквально с любого
   * состояния — переделывать нечего до принятия лабораторией и незачем после
   * того, как конструкция уже установлена (это отдельная история болезни, а
   * не тот же наряд). Разрешаю с каждого состояния, где работа реально
   * существует и ещё не передана пациенту.
   */
  ...(['accepted_by_lab', 'in_progress', 'try_in_required', 'ready', 'received_by_clinic'] as const).map(
    (from): LabOrderTransition => ({ from, to: 'remake', roles: ANY_PARTICIPANT_ROLES }),
  ),
  // Переделка возвращает наряд в работу, а не создаёт новый — счётчик и срок
  // меняются, история остаётся на одном и том же id.
  { from: 'remake', to: 'in_progress', roles: LAB_WORK_ROLES },

  // Отмена — решение клиники или лаборатории, с любого состояния до
  // установки: дальше отменять уже нечего, это случившийся факт.
  ...(
    ['created', 'accepted_by_lab', 'in_progress', 'try_in_required', 'remake', 'ready', 'received_by_clinic'] as const
  ).map((from): LabOrderTransition => ({ from, to: 'cancelled', roles: [...CLINIC_ROLES, ...LAB_MANAGEMENT_ROLES] })),
];

/**
 * Наряд без второй стороны в Odonix — «бумажный»: лаборатория заведена
 * клиникой вручную или названа от руки, своего кабинета у неё нет. Принять,
 * взять в работу и отметить готовность некому, и путь короче: клиника сама
 * отмечает, что готовую работу привезли, — дальше установка и закрытие идут
 * как у любого наряда. Переделка возвращается тем же путём.
 */
export const PAPER_ORDER_TRANSITIONS: readonly LabOrderTransition[] = [
  { from: 'created', to: 'received_by_clinic', roles: CLINIC_ROLES },
  { from: 'remake', to: 'received_by_clinic', roles: CLINIC_ROLES },
];

/**
 * Зеркальный случай: наряд лаборатории от «бумажной» клиники — заказчика,
 * заведённого лабораторией карточкой, без кабинета в Odonix. Получить работу,
 * установить конструкцию и закрыть наряд со стороны клиники некому, поэтому
 * лаборатория сама отмечает передачу работы клинике и закрывает наряд.
 * Установки здесь нет: она пишет паспорт зуба и счёт пациента, а пациент у
 * такого наряда — строка текста, не карта.
 */
export const LAB_PAPER_ORDER_TRANSITIONS: readonly LabOrderTransition[] = [
  { from: 'try_in_required', to: 'in_progress', roles: LAB_MANAGEMENT_ROLES },
  { from: 'ready', to: 'received_by_clinic', roles: LAB_MANAGEMENT_ROLES },
  { from: 'received_by_clinic', to: 'closed', roles: LAB_MANAGEMENT_ROLES },
];

/** Чья сторона у бумажного наряда: кто из двух сторон существует в Odonix. */
export type PaperSide = 'clinic' | 'lab';

const LAB_ROLE_SET: ReadonlySet<Role> = new Set<Role>(['lab_owner', 'lab_manager', 'technician']);

/** Сторона бумажного наряда по роли смотрящего: у лаборатории все роли — лабораторные. */
export function paperSideOf(role: Role): PaperSide {
  return LAB_ROLE_SET.has(role) ? 'lab' : 'clinic';
}

/**
 * Таблица переходов наряда: у бумажного — с короткой дорогой той стороны,
 * которая есть в системе.
 */
export function transitionsOf(paper: boolean, side: PaperSide = 'clinic'): readonly LabOrderTransition[] {
  if (!paper) return LAB_ORDER_TRANSITIONS;
  return side === 'lab'
    ? [...LAB_ORDER_TRANSITIONS, ...LAB_PAPER_ORDER_TRANSITIONS]
    : [...LAB_ORDER_TRANSITIONS, ...PAPER_ORDER_TRANSITIONS];
}

export interface TransitionCheck {
  from: LabOrderState;
  to: LabOrderState;
  actorRole: Role;
  /** Бумажный наряд (лаборатория без кабинета в Odonix): см. `PAPER_ORDER_TRANSITIONS`. */
  paper?: boolean | undefined;
  /** Обязательна для перехода в remake и в cancelled. */
  reason?: string | null | undefined;
  /** Обязательна для перехода в remake. */
  faultParty?: FaultParty | null | undefined;
  /** Обязательны для перехода created → accepted_by_lab. */
  technicianId?: string | null | undefined;
  dueDate?: string | null | undefined;
  /** Обязательны для перехода received_by_clinic → installed, если у наряда есть зубы. */
  installedUnitIds?: readonly string[] | undefined;
  /**
   * Есть ли у наряда единицы работы. Без них (съёмный протез, капа) установка
   * отмечается без выбора зубов — записи в паспорт зуба не будет, и требовать
   * её нечем. По умолчанию — есть: так проверка остаётся строгой там, где о
   * зубах не сказали ничего.
   */
  hasUnits?: boolean | undefined;
}

export type TransitionVerdict =
  | { ok: true }
  /** Переход уже выполнен: повтор операции из очереди, а не ошибка. */
  | { ok: false; kind: 'already'; message: string }
  | { ok: false; kind: 'illegal'; message: string };

export function checkTransition(input: TransitionCheck): TransitionVerdict {
  if (input.from === input.to) {
    return { ok: false, kind: 'already', message: `Наряд уже в состоянии «${label(input.to)}»` };
  }

  // Один и тот же переход может стоять в таблице дважды с разными ролями:
  // обычный «получено клиникой» — за клиникой, у бумажного наряда лаборатории
  // тот же шаг — за лабораторией. Роль ищется по всем строкам перехода.
  const candidates = transitionsOf(input.paper === true, paperSideOf(input.actorRole)).filter(
    (t) => t.from === input.from && t.to === input.to,
  );
  if (candidates.length === 0) {
    return {
      ok: false,
      kind: 'illegal',
      message: `Из состояния «${label(input.from)}» нельзя перейти в «${label(input.to)}»`,
    };
  }

  if (!candidates.some((t) => t.roles.includes(input.actorRole))) {
    return {
      ok: false,
      kind: 'illegal',
      message: `Роль «${input.actorRole}» не может перевести наряд в «${label(input.to)}»`,
    };
  }

  if (input.to === 'accepted_by_lab') {
    if ((input.technicianId ?? '') === '') {
      return { ok: false, kind: 'illegal', message: 'Не назначен техник' };
    }
    if ((input.dueDate ?? '') === '') {
      return { ok: false, kind: 'illegal', message: 'Не подтверждён срок изготовления' };
    }
  }

  // Причина и виновная сторона — единственный источник статистики качества:
  // без стороны непонятно, кто отвечает за брак, без причины — что именно
  // пошло не так. Оба поля обязательны, а не один на выбор.
  if (input.to === 'remake') {
    if ((input.reason ?? '').trim() === '') {
      return { ok: false, kind: 'illegal', message: 'У переделки должна быть причина' };
    }
    if (input.faultParty === null || input.faultParty === undefined) {
      return { ok: false, kind: 'illegal', message: 'Не указана виновная сторона переделки' };
    }
  }

  if (input.to === 'cancelled' && (input.reason ?? '').trim() === '') {
    return { ok: false, kind: 'illegal', message: 'У отмены наряда должна быть причина' };
  }

  if (input.to === 'installed' && input.hasUnits !== false && (input.installedUnitIds ?? []).length === 0) {
    return { ok: false, kind: 'illegal', message: 'Не указаны установленные единицы' };
  }

  return { ok: true };
}

/** Наряд просрочен, если срок прошёл и работа не передана пациенту и не закрыта. */
const OPEN_STATES: readonly LabOrderState[] = [
  'created',
  'accepted_by_lab',
  'in_progress',
  'try_in_required',
  'remake',
];

export function isOverdue(dueDate: string, status: LabOrderState, today: string): boolean {
  return OPEN_STATES.includes(status) && dueDate < today;
}

const STATE_LABEL: Record<LabOrderState, string> = {
  created: 'создан',
  accepted_by_lab: 'принят лабораторией',
  in_progress: 'в работе',
  try_in_required: 'требуется примерка',
  remake: 'переделка',
  ready: 'готов',
  received_by_clinic: 'получен клиникой',
  installed: 'установлен',
  closed: 'закрыт',
  cancelled: 'отменён',
};

export function label(state: LabOrderState): string {
  return STATE_LABEL[state];
}

const FAULT_PARTY_LABEL: Record<FaultParty, string> = {
  clinic: 'по вине клиники',
  lab: 'по вине лаборатории',
  agreed_change: 'по согласованному изменению',
};

export function faultPartyLabel(party: FaultParty): string {
  return FAULT_PARTY_LABEL[party];
}
